import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import {
  Box,
  Grid,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ThemeProvider,
  Tooltip,
  Typography,
  useMediaQuery,
  Button,
} from '@mui/material'
import { fonts, theme } from '../../../theme/DashboardTheme'
import Pagination from '@mui/material/Pagination'
import arrowimage from '@meprism/shared/src/assets/icons/next.svg'
import { useAppSelector, useAppDispatch } from '../../../redux/storeExports'
import {
  PeopleFinderSelectors, fetchBrokers
} from '@meprism/shared/src/redux/peopleFinder/PeopleFinderSlice'
import ExposedDataModal from './ExposedDataModal'
import { HelpOutlineOutlined, ArrowUpward, ArrowDownward } from '@mui/icons-material'
import {Grant} from "@meprism/shared/src/redux/product/ProductTypes";
import { getCol4DisplayValue } from '../../../utils/helpers';
import AutoCompleteSelect from './AutoCompleteSelect';
import Loader from "../atom/Loader";
import { ProfileSelectors } from '@meprism/shared/src/redux/profile/ProfileSlice'
import RemovalStatsModal from './RemovalStatsModal'

interface RemovalStatsProp {
  grant: Grant | undefined
  isLoading : boolean
}

type SortConfig = {
  key: string | null;
  direction: 'asc' | 'desc';
};

const RemovalStats = ({ grant, isLoading }: RemovalStatsProp) => {
  const shouldNotRouteToSubscriptionPage =grant && grant.grant_source === 'family_grant'
  const [selectedStatus, setSelectedStatus] = useState<string[]>([])
  const [selectedBroker, setSelectedBroker] = useState<string[]>([])
  const [searchFilters, setSearchFilters] = useState<{ brokers: string[]; statuses: string[] }>({
    brokers: [],
    statuses: [],
  });
  const accurateBrokers = useAppSelector(
    PeopleFinderSelectors.selectGroupedAccurateBrokers,
  )
  const unmatchedBrokers = useAppSelector(
    PeopleFinderSelectors.selectUnmatchedBrokers,
  )
  const user = useAppSelector(ProfileSelectors.selectProfile)

  const [page, setPage] = useState(0)
  const rowsPerPage = 10

  const shouldDisplayUpgrade=useCallback((status: string, upgrade_required: boolean)=>{
    if(!grant) return true
    if(status==='new' && upgrade_required) return true
    return false
  },[grant])

  const rows = Object.entries(accurateBrokers)
    .map(([brokerName, brokerArray], index) => {
      const inProgressRecord = brokerArray.find((item) => !['removed', 'completed'].includes(item.status))
      if (inProgressRecord) {
        const hasPhones = inProgressRecord.detail?.phones?.length ?? 0 > 0
        const hasRelatives = inProgressRecord.detail?.relatives?.length ?? 0 > 0
        const email = inProgressRecord.detail?.emails?.length ?? 0 > 0
        const address = inProgressRecord.detail?.addresses?.length ?? 0 > 0
        const name = inProgressRecord.detail?.first_name
        const hasAge = inProgressRecord.detail?.age
        const col3Text = []

        if (name) {
          col3Text.push('Name')
        }
        if (hasPhones) {
          col3Text.push('Phone')
        }
        if (hasRelatives) {
          col3Text.push('Relatives')
        }
        if (hasAge) {
          col3Text.push('Age')
        }
        if (email) {
          col3Text.push('Email')
        }
        if (address) {
          col3Text.push('Address')
        }

        const brokerlength = brokerArray.length
        const dataBroker = brokerName

        return [
          {
            id: index + 1,
            col1: dataBroker,
            col2: brokerlength,
            col3: col3Text.join(', '),
            col4: 'new',
            col5: inProgressRecord.upgrade_required,
            col6: inProgressRecord.status
          },
        ]
      }

      const removedRecord = brokerArray.find((item) => ['removed', 'completed'].includes(item.status))
      if (removedRecord) {
        const hasPhones = removedRecord.detail?.phones?.length ?? 0 > 0
        const hasRelatives = removedRecord.detail?.relatives?.length ?? 0 > 0
        const email = removedRecord.detail?.emails?.length ?? 0 > 0
        const address = removedRecord.detail?.addresses?.length ?? 0 > 0
        const name = removedRecord.detail?.first_name
        const hasAge = removedRecord.detail?.age
        const col3Text = []

        if (name) {
          col3Text.push('Name')
        }
        if (hasPhones) {
          col3Text.push('Phone')
        }
        if (hasRelatives) {
          col3Text.push('Relatives')
        }
        if (hasAge) {
          col3Text.push('Age')
        }
        if (email) {
          col3Text.push('Email')
        }
        if (address) {
          col3Text.push('Address')
        }

        const brokerlength = brokerArray.length
        const dataBroker = brokerName

        return [
          {
            id: index + 1,
            col1: dataBroker,
            col2: brokerlength,
            col3: col3Text.join(', '),
            col4: 'removed',
            col5: removedRecord.upgrade_required,
            col6: removedRecord.status
          },
        ]
      }

      const brokerRows = brokerArray.map((item, subIndex) => {
        const hasPhones = item.detail?.phones?.length ?? 0 > 0
        const hasRelatives = item.detail?.relatives?.length ?? 0 > 0
        const email = item.detail?.emails?.length ?? 0 > 0
        const address = item.detail?.addresses?.length ?? 0 > 0
        const name = item.detail?.first_name
        const hasAge = item.detail?.age
        const col3Text = []

        if (name) {
          col3Text.push('Name')
        }
        if (hasPhones) {
          col3Text.push('Phone')
        }
        if (hasRelatives) {
          col3Text.push('Relatives')
        }
        if (hasAge) {
          col3Text.push('Age')
        }
        if (email) {
          col3Text.push('Email')
        }
        if (address) {
          col3Text.push('Address')
        }

        const brokerlength = brokerArray.length
        const dataBroker = brokerName

        return {
          id: index * rowsPerPage + subIndex + 1,
          col1: dataBroker,
          col2: brokerlength,
          col3: col3Text.join(', '),
          col4: item.status,
          col5: item.upgrade_required,
          col6: item.status,
        }
      })
      return brokerRows
    })
    .flat()
    .filter(Boolean)

  const unmatchedBrokerRows = unmatchedBrokers?.map((value, index) => {
    return {
      id: rows.length + index + 1,
      col1: value,
      col2: 0,
      col3: 'None',
      col4: 'not_found',
      col5: false,
      col6: 'not_found'
    }
  })

  const isNotFoundStatus = (status: string) => status === 'not_found'

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isSmallDesktop = useMediaQuery(theme.breakpoints.down('lg'))

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage - 1)
  }
  const [openmodal, setopenmodal] = useState(false)
  const [selectedBrokerData, setSelectedBrokerData] = useState([])
  const handleOpenModal = (brokerData: any) => {
    setopenmodal(true)
    setSelectedBrokerData(brokerData)
  }
  const columns = [
    { key: 'col1', name: 'Data Brokers', resizable: true, width: 130 },
    { key: 'col2', name: 'Records found', resizable: true, width: 100 },
    { key: 'col3', name: 'Exposed Data', resizable: true, width: 200 },
    { key: 'col4', name: 'Status', resizable: true, width: 100 },
    { key: '', name: '', resizable: false, width: 5 },
  ]
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: 'asc' });
  const allRows = unmatchedBrokerRows ? rows.concat(unmatchedBrokerRows) : rows
  const filteredRows = React.useMemo(() => {
    const normalizedSelectedStatus = searchFilters.statuses.flatMap(status =>
      status === 'optout_in_progress' ? ['optout_in_progress', 'new'] : [status]
    );
  
    return allRows.filter(row =>
      (searchFilters.brokers.length === 0 || searchFilters.brokers.includes(row.col1)) &&
      (normalizedSelectedStatus.length === 0 || normalizedSelectedStatus.includes(row.col4))
    );
  }, [allRows, searchFilters]);

  useEffect(()=>{
    setPage(0)
  },[searchFilters])

  const sortRowsBasedOnTiers = useCallback(() => {
    const statusGroup = [
      'failed',
      'new',
      'removed',
      'waiting_for_confirmation',
      'optout_in_progress',
      'completed',
      'waiting_for_verification',
      ]

      const groupRows: typeof filteredRows = [];
      const upgradeRows: typeof filteredRows = [];
      const notFoundRows: typeof filteredRows = [];

      filteredRows.forEach((row) => {
        if (row.col6 === 'not_found') {
          notFoundRows.push(row);
        } else if (shouldDisplayUpgrade(row.col6, row.col5)) {
          upgradeRows.push(row);
        } else if (statusGroup.includes(row.col6)) {
          groupRows.push(row);
        }
      })

      const result: typeof filteredRows = [];
      let groupIndex = 0;
      let upgradeIndex = 0;

      while (groupIndex < groupRows.length || upgradeIndex < upgradeRows.length) {
        for (let i = 0; i < 4 && groupIndex < groupRows.length; i++) {
          result.push(groupRows[groupIndex++]);
        }

        if (upgradeIndex < upgradeRows.length) {
          result.push(upgradeRows[upgradeIndex++])
        }
      }

      return [...result, ...notFoundRows]
  },[shouldDisplayUpgrade, filteredRows])
  
  const sortedRows = React.useMemo(() => {
    const { key, direction } = sortConfig

    if (!key) {
      return sortRowsBasedOnTiers()
    }

    return [...filteredRows].sort((a, b) => {
      const rawA = a[key as keyof typeof a] as string;
      const rawB = b[key as keyof typeof b] as string;

      const formatValue = (val: string): string => {
        const str = String(val).toLowerCase();
        return key === 'col4' ? getCol4DisplayValue(str) : str;
      };

      const aVal = formatValue(rawA);
      const bVal = formatValue(rawB);

      return direction === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    });
  }, [filteredRows, sortConfig, sortRowsBasedOnTiers]);


  const paginatedRows = sortedRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage)


  const handleSort = (columnKey: string) => {
    setSortConfig((prevConfig)=>{
      if (prevConfig.key===columnKey){
        return {
          ...prevConfig,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
        }
      }
      return {
        key: columnKey,
        direction: 'asc'
      }
    })
  }
  const renderExposedData = (data: string) => {
    const returnedHtml: ReactNode[] = []
    let maxShownData = 4
    let currentIdx = 0
    const checkingKey = [
      'Name',
      'Age',
      'Address',
      'Phone',
      'Relatives',
      'Email',
      'None',
    ]
    const lenOfCheckingKey = checkingKey.length
    while (maxShownData && currentIdx < lenOfCheckingKey) {
      if (data.includes(checkingKey[currentIdx])) {
        returnedHtml.push(
          <Box
            key={maxShownData}
            sx={{
              background: '#EFEDFDB2',
              display: 'inline-block',
              padding: '8px 10px',
              borderRadius: '8px',
              marginRight: '10px',
            }}>
            {checkingKey[currentIdx]}
          </Box>,
        )
        maxShownData -= 1
      }
      currentIdx += 1
    }
    return returnedHtml
  }

  
  const statusOptions = [
    { label: 'Not Found', value: 'not_found' },
    { label: 'Removing', value: 'optout_in_progress'},
    { label: 'Removed', value: 'removed' },
  ];
  
  const brokerOptions = [
    ...Object.keys(accurateBrokers || {}).map(key => ({ label: key, value: key })),
    ...(unmatchedBrokers || []).map(broker => ({ label: broker, value: broker }))
  ];
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (user?.scan_completed_mail_sent_at) {
      console.log("Scan completed at:", user.scan_completed_mail_sent_at);
      dispatch(fetchBrokers());
    }
  }, [dispatch, user?.scan_completed_mail_sent_at]);

  const BoxShadow = '0px 4px 10px rgba(208, 210, 218, 0.37)'
  const handleSearch = () => {
    setSearchFilters({
      brokers: selectedBroker,
      statuses: selectedStatus,
    });
  }
  const fakeRows =
    user?.scan_completed_mail_sent_at === null
      ? Array.from({ length: 10 }, (_, index) => ({
          id: index + 1,
          col1: 'Data Broker',
          col2: 3,
          col3: 'Name, Phone, Email',
          col4: 'new',
          col5: false,
          col6: 'new',
        }))
      : []


  return (
    <ThemeProvider theme={theme}>
      <Loader loading={isLoading}>
      {!isMobile ? (
        <>
          {user?.scan_completed_mail_sent_at && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ mb: 2, pt: 3, pb: 2, bgcolor: 'transparent', borderRadius: 2, width: '100%' }}>
                <Grid container alignItems="center" display={'flex'}>
                  
                  {/* Filters */}
                  <Grid item xs={12} sm={10} md={10.5} sx={{ display: 'flex',
                    padding: '0 0 !important',
                    gap: {
                        sm: 1,  
                        md: 2,  
                        lg: 4,  
                      }
                    }} >
                    {/* Broker*/}
                    <Grid item xs={12} sm={6} sx={{padding: '0 2px !important'}}>
                      <AutoCompleteSelect
                        options={brokerOptions}
                        onChange={(newValue)=>{setSelectedBroker(newValue)}}
                        value={selectedBroker}
                        label="Data Broker"
                        limitTags={isSmallDesktop ? 1 : 2}
                        maxLength={30}
                      />
                    </Grid>

                    {/*Status*/}
                    <Grid item xs={12} sm={6} sx={{padding: '0 2px !important'}}>
                      <AutoCompleteSelect
                        options={statusOptions}
                        onChange={(newValue)=>{setSelectedStatus(newValue)}}
                        value={selectedStatus}
                        label="Status"
                        limitTags={isSmallDesktop ? 1 : 3}
                        maxLength={20}
                      />  
                    </Grid>
                  </Grid>

                  {/*Search button*/}
                  <Grid item xs={12} sm={2} md={1.5} sx={{padding: '0 2px !important', display: 'flex', justifyContent: 'flex-end'}}>
                    <Button 
                      sx={{
                        width: '163px', 
                        minHeight: '40px',
                        maxHeight: '40px', 
                        fontFamily: 'InterMedium', 
                        backgroundColor: "#9135E0",  
                        color: "#fff",
                        "&:hover": {
                          backgroundColor: "#7a29c9",
                        },
                        fontSize: {sm: '15px'}
                      }} 
                      variant="contained" 
                      onClick={handleSearch}>
                      Search
                    </Button>
                  </Grid>
                </Grid>    
              </Box>
            </Box>
          )}
          <Box
            style={{ 
              height: 'auto', 
              width: '100%', 
              paddingBottom: '16px',
              boxShadow: BoxShadow,
              position: 'relative'
            }}
            bgcolor="#fff"
            borderRadius={5}
            p={5}
            mb={5}
            >
            <TableContainer>
              <Box sx={{ mb: 1, fontSize: 20, lineHeight: '27px', fontWeight: 500, fontFamily: "InterMedium", color: '#1A1B20' }}>
                Websites selling your data
              </Box>
              <div style={{ 
                  position: 'relative'
                }}
              >
                {(user?.scan_completed_mail_sent_at || grant?.is_executive) ? (
                  <Table
                    style={{
                      background: '#fff',
                      color: '#040115',
                      borderRadius: 5,
                      padding: 3,
                      tableLayout: 'fixed',
                    }}>
                    <TableHead>
                      <TableRow>
                        {columns.map((column) => (
                          <TableCell
                            key={column.key}
                            style={{
                              borderBottom: '1px solid #9135E0',
                              ...fonts.Inter.SemiBold,
                              fontWeight: 600,
                              textAlign: 'left',
                              color:'#040115',
                              fontSize:14,
                              width: column.width,
                              position: 'relative',
                              padding: '16px 16px 3px 1px'
                            }}>
                            {column.name}
                            {column.name === 'Records found' && (
                              <Tooltip 
                                title="A data broker can create multiple records or profiles of you using your data"
                                placement="top"
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      fontFamily: 'InterRegular',
                                      fontSize: '10px',
                                      lineHeight: '16px',
                                      fontWeight: 500,
                                      color: '#ffffff',
                                    }
                                  },
                                }}
                                >
                                <IconButton>
                                  <HelpOutlineOutlined
                                    sx={{
                                      position: 'absolute', 
                                      left: '50%',
                                      top: '-4px',
                                      fontSize: '18px',
                                      color: '#9135E0',
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                            {(['col1', 'col4'].includes(column.key)) && (
                              <IconButton size="small" onClick={()=> handleSort(column.key)}>
                                {sortConfig.key === column.key && sortConfig.direction === 'desc' ? (
                                  <ArrowUpward sx={{ fontSize: '16px', color: 'rgba(145, 53, 224, 1)' }} />
                                ) : (
                                  <ArrowDownward sx={{ fontSize: '16px', color: 'rgba(145, 53, 224, 1)' }} />
                                )}
                              </IconButton>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {paginatedRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={columns.length} style={{ textAlign: 'center', padding: '16px 16px 16px 0'}}>
                            {allRows.length === 0 ? (
                              'Scanning in progress'
                            ) : (
                              'No profiles match these criteria'
                            )}
                          </TableCell>
                        </TableRow>
                      ):(
                        paginatedRows.map((row) => (
                          <TableRow key={row.id}>
                          {columns.map((column) => (
                            <TableCell
                              onClick={() => {
                                const brokerData = accurateBrokers[row.col1]
                                if (grant && !isNotFoundStatus(row.col4)) {
                                  handleOpenModal(brokerData)
                                }
                              }}
                              key={column.key}
                              style={{
                                borderBottom: '1px solid #615F6B',
                                fontFamily: 'InterMedium',
                                fontWeight: 500,
                                width: column.width,
                                padding: '16px 10px 10px 1px',
                              }}>
                              {column.key === '' && grant && !isNotFoundStatus(row.col4) ? (
                                <img
                                  src={arrowimage}
                                  alt="Arrow"
                                  style={{ width: '20px', height: '20px' }}
                                />
                              ) : column.key === 'col1' ? (
                                `${row.col1}`
                              ) : column.key === 'col3' ? (
                                <>{renderExposedData(row.col3)}</>
                                ) : column.key === 'col4' ? (
                                  isNotFoundStatus(row.col4) ? (
                                    <span style={{ color: '#E2B93B' }}>Not Found</span>
                                  ) :
                                  !shouldDisplayUpgrade(row.col6, row.col5) ? (
                                  <span
                                    style={{
                                      color: ['optout_in_progress', 'new'].includes(
                                        row.col4,
                                      )
                                        ? '#E2B93B'
                                        : '#27AE60',
                                    }}>
                                    {
                                      ['optout_in_progress', 'new'].includes(
                                        row.col4,
                                      )
                                        ? 'Removing'
                                        : 'Removed' // Adjust with actual status names
                                    }
                                  </span>
                                ) : (
                                  shouldNotRouteToSubscriptionPage?
                                  <Typography sx={{fontFamily:"InterMedium", color:'#426BF0', fontSize:14 }}>
                                      Upgrade to Opt Out
                                  </Typography>
                                  :
                                  <Link href="/subscription" underline="none">
                                    <Typography sx={{ textDecoration: 'underline', fontFamily:"InterMedium", color:'#426BF0', fontSize:14 }}>
                                      {!grant ? 'Subscribe to Remove' : 'Upgrade to Remove'}
                                    </Typography>
                                  </Link>
                                )
                              ) : (
                                row[column.key as keyof typeof row]
                              )}
                            </TableCell>
                          ))}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                  ) : (
                  <Table>
                    <TableHead>
                      <TableRow>
                        {columns.map((column) => (
                          <TableCell key={column.key}>{column.name}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fakeRows.map((row) => (
                        <TableRow key={row.id}>
                          {columns.map((column) => (
                            <TableCell 
                              style={{
                                borderBottom: '1px solid grey',
                              }}                                                
                              key={column.key}
                            >
                              {column.key === 'col3' ? renderExposedData(row.col3) : row[column.key as keyof typeof row]}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
              {(user?.scan_completed_mail_sent_at || grant?.is_executive) && (

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 2,
                  padding: 2,
                  borderRadius: 5,
                  background: '#fff',
                }}>
                <Pagination
                  variant="outlined"
                  shape="rounded"
                  count={Math.ceil(sortedRows.length / rowsPerPage)}
                  page={page + 1}
                  onChange={handleChangePage}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      fontSize: '14px',
                      fontWeight: 600,
                      fontFamily: 'InterSemiBold',
                    },
                     '& .MuiPaginationItem-root.Mui-selected': {
                        backgroundColor: 'transparent',
                        color: '#426BF0',
                        fontWeight: 600,
                        fontSize: '14px',
                        fontFamily: 'InterSemiBold',
                        border: '1px solid #426BF0',
                      },
                       '& .MuiPaginationItem-previousNext.Mui-disabled': {
                        backgroundColor: '#c8ced5',
                      },
                  }}
                />
              </Box>)}
            </TableContainer>
            {(!user?.scan_completed_mail_sent_at && !grant?.is_executive) && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backdropFilter: 'blur(4px)',
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  borderRadius: 5,
                }}
              >
                <RemovalStatsModal/>
              </Box>
            )}
          </Box>
        </>
      ) : (
        <div style={{
          position: 'relative'
        }}>

          {user?.scan_completed_mail_sent_at ? (
            <Box
              style={{ height: 'auto', width: '100%', position: 'relative' }}
              bgcolor="#fff"
              borderRadius={5}
              boxShadow={BoxShadow}
              p={3}>
              <Typography variant="h3" mx={2} mt={2} mb={5}>
                Removal Stats({allRows.length})
              </Typography>

              {allRows.map((row) => (
                <Grid
                  key={row?.id}
                  container
                  direction="row"
                  spacing={2}
                  sx={{
                    borderBottom: '1px solid #615F6B',
                    padding: 2,
                    position: 'relative',
                  }}>
                  {columns.map((col) => (
                    <Grid
                      item
                      key={col.key}
                      xs={6}
                      onClick={() => {
                        if (row) {
                          const brokerData = accurateBrokers[row.col1]
                          if (grant && !isNotFoundStatus(row.col4)) {
                            handleOpenModal(brokerData)
                          }
                        }
                      }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'left',
                          wordWrap: 'break-word',
                        }}>
                        {col.key !== '' && (
                          <Typography variant="h5">{col.name}:</Typography>
                        )}

                        {col.key === 'col3' ? (
                          <Typography variant="label">
                            {renderExposedData(
                              row?.col3 !== undefined ? row.col3 : '',
                            )}
                          </Typography>
                        ) : col.key === 'col4' ? (
                          !shouldDisplayUpgrade(row.col6, row.col5) ? (
                            <Typography
                              variant="label"
                              color={
                                [
                                  'optout_in_progress',
                                  'new',
                                  'waiting_for_verification',
                                ].includes(row?.col4 || '')
                                  ? '#E2B93B'
                                  : isNotFoundStatus(row.col4) ? '#E2B93B' : '#EB5757'
                              }>
                              {
                                [
                                  'optout_in_progress',
                                  'new',
                                  'waiting_for_verification',
                                ].includes(row?.col4 || '')
                                  ? 'Removing'
                                  : isNotFoundStatus(row.col4) ? 'None' : 'Removed' // Adjust with actual status names
                              }
                            </Typography>
                          ) : (
                            shouldNotRouteToSubscriptionPage?
                            <Typography sx={{fontFamily:"InterMedium", color:'#426BF0', fontSize:14 }}>
                                Upgrade to Opt Out
                            </Typography>
                            :
                            <Link href="/subscription" underline="none">
                              <Typography sx={{ textDecoration: 'underline', fontFamily:"InterMedium", color:'#426BF0', fontSize:14 }}>
                                  {!grant ? 'Subscribe to Remove' : 'Upgrade to Remove'}
                              </Typography>
                            </Link>
                          )
                        ) : (
                          <Typography variant="label">
                            {' '}
                            {row?.[col.key as keyof typeof row]}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  ))}
                  {grant && !isNotFoundStatus(row.col4) && (
                    <Box>
                      {' '}
                      <img
                        src={arrowimage}
                        alt="Arrow"
                        onClick={() => {
                          if (row) {
                            const brokerData = accurateBrokers[row.col1]
                            handleOpenModal(brokerData)
                          }
                        }}
                        style={{
                          marginBottom: '2rem',
                          width: '20px',
                          height: '20px',
                          position: 'absolute',
                          bottom: '0',
                          right: '0',
                        }}
                      />
                    </Box>
                  )}
                </Grid>
              ))}
            </Box>
          ) : (
            fakeRows.map((row) => (
              <Grid
                key={row.id}
                container
                direction="row"
                spacing={2}
                sx={{
                  borderBottom: '1px solid #615F6B',
                  padding: 2,
                  position: 'relative',
                }}
              >
                {columns.map((col) => (
                  <Grid item key={col.key} xs={6}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'left',
                        wordWrap: 'break-word',
                      }}
                    >
                      {col.key !== '' && <Typography variant="h5">{col.name}:</Typography>}
                      {col.key === 'col3' ? (
                        <Typography variant="label">{renderExposedData(row?.col3 || '')}</Typography>
                      ) : (
                        <Typography variant="label">{row?.[col.key as keyof typeof row]}</Typography>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ))
          )}
            {(!user?.scan_completed_mail_sent_at && !grant?.is_executive) && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'transparent',
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <RemovalStatsModal/>
              </Box>
            )}
        </div>
      )}
      </Loader>
      <ExposedDataModal
        rows={selectedBrokerData}
        open={openmodal}
        setOpen={setopenmodal}
      />
    </ThemeProvider>
  )
}

export default RemovalStats
